import { useEffect, useState } from "react";
import { Grid, Stack, Box, CircularProgress, Alert, Typography, Radio, Divider, Chip, Tooltip, IconButton, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import { ShoppingCart, Info, Gift } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PageProtectedOnboarding from "../../../components/Page/PageProtectedOnboarding";
import { pool, API_URL } from "../../../constants";
import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import OnbSteps from "../../../components/Steps/OnbSteps";
import ButtonForm from "../../../components/Button/ButtonForm";
import ButtonsMain from "../../../components/Button/ButtonMain";
import CustomDialog from "../../../components/Dialog/DiaCompMedium";
import CoreBloodTestExplain from "./ShopCardsMarker/CoreBloodTestExplain";
import Apple_Pay from '../../../assets/Apple_Pay.svg';
import Klarna from '../../../assets/icons/Klarna.png';

function cardSx(theme, accentColor) {
  return {
    border: '1px solid',
    borderRadius: 4,
    overflow: 'hidden',
    borderLeft: `10px solid ${accentColor}`,
    ...(theme.palette.mode === 'light'
      ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
      : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
  };
}

// Step badge for numbered list items (CLAUDE.md design system pattern)
function NumberedSection({ number, heading, children }) {
  const theme = useTheme();
  const numberColor = theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100;
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 2 }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 28, height: 28,
          borderRadius: '50%',
          border: `2px solid ${numberColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mt: '2px',
        }}
      >
        <Typography variant="number4" sx={{ color: numberColor, fontWeight: 400, fontSize: '0.75rem', lineHeight: 1 }}>
          {number}
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={400} sx={{ mb: 1.5 }}>{heading}</Typography>
        {children}
      </Box>
    </Stack>
  );
}

function SelectableRow({ selected, onClick, title, price, badge, accentColor }) {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        py: 0.5, px: 2, borderRadius: 4, overflow: 'hidden', cursor: 'pointer',
        border: '1px solid',
        borderLeft: `10px solid ${selected ? theme.palette.primary.main : accentColor}`,
        ...(theme.palette.mode === 'light'
          ? { borderColor: 'grey.C900' }
          : { borderColor: 'greydark.C400' }),
        bgcolor: selected
          ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.08 : 0.16)
          : (theme.palette.mode === 'light' ? 'grey.C50' : 'greydark.C900'),
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <Radio checked={selected} onChange={onClick} size="small" sx={{ py: 0.5 }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={400}>{title}</Typography>
      </Box>
      {badge && (
        <Chip
          label={badge}
          size="small"
          sx={{
            bgcolor: theme.palette.traffic.green,
            color: '#fff',
            fontWeight: 400,
            fontSize: '0.65rem',
            height: 22,
            borderRadius: '6px',
            flexShrink: 0,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      )}
      {price != null && (
        <Typography variant="body2" fontWeight={400} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          € {Number(price).toFixed(0)},-
        </Typography>
      )}
    </Box>
  );
}

export default function OnbSubscription() {
  const { state } = useAuth();
  const { t, changeLanguage } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [frequency, setFrequency] = useState("2x"); // "2x" | "4x"
  const [paymentMethod, setPaymentMethod] = useState("full"); // "full" | "monthly"

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coreOpen, setCoreOpen] = useState(false);

  useEffect(() => {
    if (state.user?.language) {
      changeLanguage(state.user.language);
    }
  }, [state.user?.language]);

  useEffect(() => {
    async function fetchPlans() {
      try {
        const response = await axios.get(`${API_URL}/api/subproducts/`);
        setPlans(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
        setPlans([]);
      } finally {
        setPlansLoading(false);
      }
    }
    fetchPlans();
  }, []);

  const PLAN_OPTION_MAP = {
    "2x_full": "2",
    "2x_monthly": "4",
    "4x_full": "3",
    "4x_monthly": "5",
  };
  const planOption = PLAN_OPTION_MAP[`${frequency}_${paymentMethod}`];
  const getPlanPrice = (planOptionValue) =>
    plans.find((p) => p.plan_option === planOptionValue)?.price_eur;

  const price2xFull = getPlanPrice(PLAN_OPTION_MAP["2x_full"]);
  const price4xFull = getPlanPrice(PLAN_OPTION_MAP["4x_full"]);
  const fullPrice = frequency === "4x" ? price4xFull : price2xFull;
  const monthlyPrice = getPlanPrice(PLAN_OPTION_MAP[`${frequency}_monthly`]);
  const selectedPrice = paymentMethod === "monthly" ? monthlyPrice : fullPrice;

  const saveAmount = fullPrice && monthlyPrice
    ? Math.round(Number(monthlyPrice) * 12 - Number(fullPrice))
    : null;

  const handleCheckout = async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/onboarding/subscription/checkout/`, { planOption });
      window.location.href = response.data.checkout_url;
    } catch (err) {
      const backendError = err.response?.data?.error;
      const KNOWN_BACKEND_ERRORS = {
        "Missing planOption.": t("onbsub_error_missing_plan"),
        "Invalid plan selected": t("onbsub_error_invalid_plan"),
      };
      setError(KNOWN_BACKEND_ERRORS[backendError] || backendError || t("onb_error_checkout"));
    } finally {
      setLoading(false);
    }
  };

  const accentLeft = theme.palette.tertiary.C300;
  const accentRight = theme.palette.quartiary.C300;

  return (
    <PageProtectedOnboarding
      headerImage={pool}
      brandtext={t("onbsub_brandtext")}
      content={
        <>
          <Grid container sx={{ mb: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Grid size={12} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <OnbSteps stepNumber={0} />
            </Grid>
          </Grid>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{ mb: 4, px: { xs: 1, sm: 2 } }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Gift size={20} color={accentRight} />
              <Typography variant="subtitle1" fontWeight={400}>{t("onbgift_prompt")}</Typography>
            </Stack>
            <ButtonsMain
              name={t("onbgift_btn_open")}
              action={() => navigate("/OnbGiftCode")}
              sx={{ px: '24px', mt: 0 }}
            />
          </Stack>

          <Grid container spacing={4} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, mx: 'auto' }}>
            {/* Question box */}
            <Grid size={12}>
              <Box sx={{ px: 3 }}>
                <NumberedSection number={1} heading={t("onbsub_freq_question")}>
                  <Stack spacing={1.5}>
                    <SelectableRow
                      selected={frequency === "2x"}
                      onClick={() => setFrequency("2x")}
                      title={t("onbsub_freq_2x")}
                      accentColor={accentLeft}
                    />
                    <SelectableRow
                      selected={frequency === "4x"}
                      onClick={() => setFrequency("4x")}
                      title={t("onbsub_freq_4x")}
                      accentColor={accentLeft}
                    />
                  </Stack>
                </NumberedSection>

                <NumberedSection number={2} heading={t("onbsub_pay_question")}>
                  {plansLoading ? (
                    <Grid container sx={{ justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Grid>
                  ) : (
                    <Stack spacing={1.5}>
                      <SelectableRow
                        selected={paymentMethod === "full"}
                        onClick={() => setPaymentMethod("full")}
                        title={t("onbsub_pay_title_full")}
                        price={fullPrice}
                        badge={saveAmount > 0 ? t("onbsub_save_badge").replace("{{amount}}", saveAmount) : null}
                        accentColor={accentLeft}
                      />
                      <SelectableRow
                        selected={paymentMethod === "monthly"}
                        onClick={() => setPaymentMethod("monthly")}
                        title={t("onbsub_pay_title_monthly")}
                        price={monthlyPrice}
                        accentColor={accentLeft}
                      />
                    </Stack>
                  )}
                </NumberedSection>

                <NumberedSection number={3} heading={t("onbsub_review_heading")}>
                  <Box sx={{ ...cardSx(theme, accentRight), p: 3 }}>
                    <Typography variant="h6" fontWeight={500} sx={{ mb: 2 }}>
                      {t("onbsub_summary_title")}
                    </Typography>

                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                          bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <ShoppingCart size={20} color={accentRight} />
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="subtitle1" fontWeight={400}>
                            {t("onbsub_summary_subscription").replace("{{count}}", frequency === "4x" ? "4" : "2")}
                          </Typography>
                          <Tooltip title={t("onbsub_tooltip_core_test")} placement="right">
                            <IconButton size="small" onClick={() => setCoreOpen(true)}>
                              <Info size={18} color={theme.palette.secondary.dark} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                          {t("onbsub_summary_visits_label")} {frequency === "4x" ? t("onbsub_freq_4x") : t("onbsub_freq_2x")} · {paymentMethod === "monthly" ? t("onbsub_pay_title_monthly") : t("onbsub_pay_title_full")}
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                      <Typography fontWeight={400}>{t("onbsub_summary_total")}</Typography>
                      <Typography variant="h5" fontWeight={400}>
                        {selectedPrice ? `€ ${Number(selectedPrice).toFixed(0)},-` : '—'}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      {t("onbsub_note_vat")}
                    </Typography>

                    {loading ? (
                      <Grid container sx={{ justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={24} />
                      </Grid>
                    ) : (
                      <>
                        <ButtonForm name={t("onb_btn_secure_payment")} onClick={handleCheckout} disabled={loading} />

                        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2, mb: 1 }}>
                          <PaymentIcon type="visa" format="flatRounded" width={36} />
                          <PaymentIcon type="mastercard" format="flatRounded" width={36} />
                          <PaymentIcon type="paypal" format="flatRounded" width={36} />
                          <Box mb={1}>
                            <div><img src={Apple_Pay} alt="ApplePay" width={36} height={27} /></div>
                          </Box>
                          <Box mb={1}>
                            <div><img src={Klarna} alt="Klarna" height={22} /></div>
                          </Box>
                        </Stack>

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                      </>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" color="text.secondary">
                      {[
                        t("onbsub_note_term"),
                        t("onbsub_note_renewal"),
                        t("onbsub_note_cancel"),
                      ].join(" ")}
                    </Typography>

                    <CustomDialog
                      open={coreOpen}
                      onClose={() => setCoreOpen(false)}
                      title=""
                      content={
                        <Grid container>
                          <Grid size={12} sx={{ display: "flex", justifyContent: "center" }}>
                            <CoreBloodTestExplain />
                          </Grid>
                        </Grid>
                      }
                    />
                  </Box>
                </NumberedSection>
              </Box>
            </Grid>
          </Grid>
        </>
      }
    />
  );
}
